import { ChatCommand } from '$tablet-app/src/app/chat/commands/types';
import ChatScreenView from '$tablet-app/src/app/chat/components/ChatScreenView';
import { useCurrentUser } from '$tablet-app/src/app/users';
import { useCommands } from '@akin-home/commands-react';
import React, { useCallback, useRef, useState } from 'react';
import { Animated, Easing } from 'react-native';
import { useApplicationID } from '../chat/useAkinChat';
import {
  startVoiceListening,
  stopVoiceListening,
} from '../speech-to-text/useSTT';
import { useStateStore } from '../stores/useStateStore';

interface Props {
  minSize?: boolean;

  onClose?(): void;
}

const ChatScreen = (props: Props) => {
  const { minSize, onClose } = props;

  const listening = useStateStore((state) => state.listening_to_voice);
  const clearChat = useStateStore((state) => state.clearChat);
  const items = useStateStore((state) => state.chat_items);
  const post = useStateStore((state) => state.sendChatMessage);
  const selectOption = useStateStore((state) => state.selectChatOption);
  const application_id = useApplicationID();

  const [chatInput, setChatInput] = useState('');
  const [expression, setExpression] = useState('default');
  const [longMicPress, setLongMicPress] = useState(false);
  //controls the animation of the size and visibility of the avatar
  const visibilityAnim = useRef(new Animated.Value(1)).current;
  const sizeAnim = useRef(new Animated.Value(1)).current;

  const two_columns = !minSize;

  // Assuming pixi dev application_id is set to 3
  const isPixiDev = application_id === 3;

  useCommands(
    [
      'set_expression',
      'minimise_avatar',
      'maximise_avatar',
      'show_avatar',
      'hide_avatar',
    ],
    useCallback(
      (commands: ChatCommand[]) => {
        commands.forEach((cmd) => {
          switch (cmd.type) {
            case 'set_expression':
              setExpression(cmd.data.expression);

              // Set back to default after a specified time
              setTimeout(() => {
                setExpression('default');
              }, 3000);
              break;
            case 'minimise_avatar':
              controlAvatarAnimatedTimings(sizeAnim, 0.2);
              break;
            case 'maximise_avatar':
              controlAvatarAnimatedTimings(sizeAnim, 1);
              break;
            case 'show_avatar':
              controlAvatarAnimatedTimings(visibilityAnim, 1);
              break;
            case 'hide_avatar':
              controlAvatarAnimatedTimings(visibilityAnim, 0);
              break;
          }
        });
      },
      [sizeAnim, visibilityAnim]
    )
  );

  const user = useCurrentUser();

  //handles the animation timings of the avatar
  const controlAvatarAnimatedTimings = (
    animatedValue: Animated.Value,
    toValue: number
  ) => {
    Animated.timing(animatedValue, {
      toValue: toValue,
      easing: Easing.linear,
      useNativeDriver: false, // Was getting occasional errors when this was set to true
    }).start();
  };

  const onSubmit = async () => {
    // Only do stuff if there is chat input :P
    if (!chatInput) {
      return;
    }
    post(chatInput);
    setChatInput('');
  };

  /** Sends the message silently */
  const onSilentSend = () => {
    if (!chatInput) {
      return;
    }
    post(chatInput);
    setChatInput('');
  };

  /** On quick mic press, start listening for 3 seconds */
  const onMicPress = () => startVoiceListening();

  /** IF there is a long press, we want to keep the
   * mic active for the duration of the button press */
  const onLongPress = () => {
    setLongMicPress(true);
    startVoiceListening();
  };

  /** Stop the mic if it was a long press */
  const onMicPressOut = () => {
    if (longMicPress) {
      setLongMicPress(false);
      stopVoiceListening();
    }
  };

  const onSelectChatOption = useCallback((input: string, idx: number) => {
    selectOption(input, { idx });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ChatScreenView
      user={user}
      avatar_expression={expression}
      chat_items={items}
      chat_input={chatInput}
      mic_active={listening}
      two_columns={two_columns}
      isPixiDev={isPixiDev}
      onClose={onClose}
      onSubmit={onSubmit}
      onChangeInput={setChatInput}
      onSelectChatOption={onSelectChatOption}
      onMicPress={onMicPress}
      onMicLongPress={onLongPress}
      onMicPressOut={onMicPressOut}
      onClearPress={clearChat}
      onSilentSend={onSilentSend}
    />
  );
};
export default ChatScreen;
